import { tKey } from 'ember-intl';
import {
  BENI_COMPARISON_SETS,
  BENI_MARKET_CLASS,
  type ComparisonSet,
} from 'frontend-utils/utils/constants/benchmark-insight';
import {
  COLOR_BORDER_DEFAULT,
  COLOR_CHART_SEQUENTIAL_INFO_04,
  COLOR_CHART_SEQUENTIAL_INFO_09,
  COLOR_CHART_SEQUENTIAL_NEUTRAL_04,
  COLOR_CHART_SEQUENTIAL_NEUTRAL_09,
  COLOR_CHART_SEQUENTIAL_WARNING_02,
  COLOR_CHART_SEQUENTIAL_WARNING_05,
  COLOR_TEXT_DEFAULT,
  COLOR_TEXT_SUBDUED,
  getCssVariableString,
} from 'frontend-utils/utils/css-variable';

export const ALL_SEGMENTS_OPTION = {
  value: tKey('benchmark_insight.segment.all_segments'),
  id: 'all_segments',
};

export const KPIS = {
  OCCUPANCY: 'occupancy',
  OCCUPANCY_MIX: 'occupancyMix',
  ADR: 'adr',
  REVPAR: 'revpar',
} as const;
export type Kpi = (typeof KPIS)[keyof typeof KPIS];

export const KPI_LONG_TRANSLATIONS: Record<Kpi, string> = {
  [KPIS.OCCUPANCY]: tKey('benchmark_insight.occupancy'),
  [KPIS.OCCUPANCY_MIX]: tKey('benchmark_insight.occupancy_mix'),
  [KPIS.ADR]: tKey('benchmark_insight.adr'),
  [KPIS.REVPAR]: tKey('benchmark_insight.revpar'),
};

export const KPI_SHORT_TRANSLATIONS: Partial<Record<Kpi, string>> = {
  [KPIS.OCCUPANCY]: tKey('benchmark_insight.occupancy.short'),
};

export const KPI_INDEXES = {
  MPI: 'mpi',
  ARI: 'ari',
  RGI: 'rgi',
} as const;

// e.g. MPI
export const KPI_INDEX_TRANSLATIONS = {
  [KPI_INDEXES.MPI]: tKey('benchmark_insight.mpi'),
  [KPI_INDEXES.ARI]: tKey('benchmark_insight.ari'),
  [KPI_INDEXES.RGI]: tKey('benchmark_insight.rgi'),
} as const;

// e.g. Market Penetration Index
export const KPI_INDEX_LONG_TRANSLATIONS = {
  [KPI_INDEXES.MPI]: tKey('benchmark_insight.market_penetration_index'),
  [KPI_INDEXES.ARI]: tKey('benchmark_insight.average_rate_index'),
  [KPI_INDEXES.RGI]: tKey('benchmark_insight.revenue_generation_index'),
};

// e.g. Market Penetration Index (MPI)
export const KPI_INDEX_LONG_AND_ABBREVIATED_TRANSLATIONS = {
  [KPI_INDEXES.MPI]: tKey('benchmark_insight.hero_chart.mpi'),
  [KPI_INDEXES.ARI]: tKey('benchmark_insight.hero_chart.ari'),
  [KPI_INDEXES.RGI]: tKey('benchmark_insight.hero_chart.rgi'),
};

export const KPI_INDEX_INFO_TOOLTIP_TRANSLATIONS = {
  [KPI_INDEXES.MPI]: tKey('benchmark_insight.legend.info_tooltip.mpi'),
  [KPI_INDEXES.ARI]: tKey('benchmark_insight.legend.info_tooltip.ari'),
  [KPI_INDEXES.RGI]: tKey('benchmark_insight.legend.info_tooltip.rgi'),
};

export const KPI_INDEXOPTION = {
  KPI_DIFFERENCE: 'kpi_difference',
  INDEX: 'index',
} as const;
export type HeroChartAxisOption =
  (typeof KPI_INDEXOPTION)[keyof typeof KPI_INDEXOPTION];

export const FORMATTED_KPIS = {
  [KPIS.OCCUPANCY]: 'formattedOccupancy',
  [KPIS.OCCUPANCY_MIX]: 'formattedOccupancyMix',
  [KPIS.ADR]: 'formattedAdr',
  [KPIS.REVPAR]: 'formattedRevpar',
} as const;

export const KPI_CATEGORIES = {
  TRANSIENT: 'transient',
  RETAIL: 'retail',
  DIRECT: 'direct',
  NON_DIRECT: 'non_direct',
  DISCOUNT: 'discount',
  GENERAL_DISCOUNT: 'general_discount',
  PROMOTION: 'promotion',
  PACKAGE_PATH_OPAQUE: 'package_path_opaque',
  NEGOTIATED: 'negotiated',
  CORPORATE_DISCOUNT: 'corporate_discount',
  CORPORATE_NEGOTIATED: 'corporate_negotiated',
  GOVERNMENT: 'government',
  WHOLESALE: 'wholesale',
  QUALIFIED: 'qualified',
  GROUP: 'group',
  BUSINESS: 'business',
  LEISURE: 'leisure',
  CONTRACTED_CREW: 'contracted_crew',
  OTHER: 'other',
} as const;
export type KpiCategory = (typeof KPI_CATEGORIES)[keyof typeof KPI_CATEGORIES];
export const isKpiCategory = (value: string): value is KpiCategory =>
  Object.values(KPI_CATEGORIES).includes(value as KpiCategory);

export const FORMATTED_KPI_INDEXES = {
  [KPI_INDEXES.MPI]: 'formattedMpi',
  [KPI_INDEXES.ARI]: 'formattedAri',
  [KPI_INDEXES.RGI]: 'formattedRgi',
} as const;

export const KPI_INDEXES_FOR_KPIS = {
  [KPIS.OCCUPANCY]: KPI_INDEXES.MPI,
  [KPIS.OCCUPANCY_MIX]: KPI_INDEXES.MPI,
  [KPIS.ADR]: KPI_INDEXES.ARI,
  [KPIS.REVPAR]: KPI_INDEXES.RGI,
} as const;
export type KpiIndexType =
  (typeof KPI_INDEXES_FOR_KPIS)[keyof typeof KPI_INDEXES_FOR_KPIS];

export const DEFINITIONS = {
  'benchmark_insight.definition.retail_direct': {
    name: 'retail_direct',
    description: tKey('benchmark_insight.definition.retail_direct.description'),
    points: [
      tKey('benchmark_insight.definition.retail_direct.point1'),
      tKey('benchmark_insight.definition.retail_direct.point2'),
      tKey('benchmark_insight.definition.retail_direct.point3'),
      tKey('benchmark_insight.definition.retail_direct.point4'),
    ],
  },
  'benchmark_insight.definition.retail_non_direct': {
    name: 'retail_non_direct',
    description: tKey(
      'benchmark_insight.definition.retail_non_direct.description',
    ),
    points: [tKey('benchmark_insight.definition.retail_non_direct.point1')],
  },
  'benchmark_insight.definition.direct_general_discount': {
    name: 'direct_general_discount',
    description: tKey(
      'benchmark_insight.definition.direct_general_discount.description',
    ),
    points: [
      tKey('benchmark_insight.definition.direct_general_discount.point1'),
      tKey('benchmark_insight.definition.direct_general_discount.point2'),
      tKey('benchmark_insight.definition.direct_general_discount.point3'),
    ],
  },
  'benchmark_insight.definition.direct_promotion': {
    name: 'direct_promotion',
    description: tKey(
      'benchmark_insight.definition.direct_promotion.description',
    ),
    points: [
      tKey('benchmark_insight.definition.direct_promotion.point1'),
      tKey('benchmark_insight.definition.direct_promotion.point2'),
    ],
  },
  'benchmark_insight.definition.non_direct_general_discount': {
    name: 'non_direct_general_discount',
    description: tKey(
      'benchmark_insight.definition.non_direct_general_discount.description',
    ),
    points: [
      tKey('benchmark_insight.definition.non_direct_general_discount.point1'),
    ],
  },
  'benchmark_insight.definition.non_direct_promotion': {
    name: 'non_direct_promotion',
    description: tKey(
      'benchmark_insight.definition.non_direct_promotion.description',
    ),
    points: [tKey('benchmark_insight.definition.non_direct_promotion.point1')],
  },
  'benchmark_insight.definition.non_direct_package': {
    name: 'non_direct_package',
    description: tKey(
      'benchmark_insight.definition.non_direct_package.description',
    ),
    points: [
      tKey('benchmark_insight.definition.non_direct_package.point1'),
      tKey('benchmark_insight.definition.non_direct_package.point2'),
    ],
  },
  'benchmark_insight.definition.corporate_discount': {
    name: 'corporate_discount',
    description: tKey(
      'benchmark_insight.definition.corporate_discount.description',
    ),
    points: [],
  },
  'benchmark_insight.definition.corporate_negotiated': {
    name: 'corporate_negotiated',
    description: tKey(
      'benchmark_insight.definition.corporate_negotiated.description',
    ),
    points: [
      tKey('benchmark_insight.definition.corporate_negotiated.point1'),
      tKey('benchmark_insight.definition.corporate_negotiated.point2'),
    ],
  },
  'benchmark_insight.definition.government': {
    name: 'government',
    description: tKey('benchmark_insight.definition.government.description'),
    points: [],
  },
  'benchmark_insight.definition.qualified': {
    name: 'qualified',
    description: tKey('benchmark_insight.definition.qualified.description'),
    points: [
      tKey('benchmark_insight.definition.qualified.point1'),
      tKey('benchmark_insight.definition.qualified.point2'),
      tKey('benchmark_insight.definition.qualified.point3'),
      tKey('benchmark_insight.definition.qualified.point4'),
      tKey('benchmark_insight.definition.qualified.point5'),
      tKey('benchmark_insight.definition.qualified.point6'),
      tKey('benchmark_insight.definition.qualified.point7'),
    ],
  },
  'benchmark_insight.definition.group_business': {
    name: 'group_business',
    description: tKey(
      'benchmark_insight.definition.group_business.description',
    ),
    points: [
      tKey('benchmark_insight.definition.group_business.point1'),
      tKey('benchmark_insight.definition.group_business.point2'),
      tKey('benchmark_insight.definition.group_business.point3'),
      tKey('benchmark_insight.definition.group_business.point4'),
      tKey('benchmark_insight.definition.group_business.point5'),
      tKey('benchmark_insight.definition.group_business.point6'),
      tKey('benchmark_insight.definition.group_business.point7'),
    ],
  },
  'benchmark_insight.definition.group_leisure': {
    name: 'group_leisure',
    description: tKey('benchmark_insight.definition.group_leisure.description'),
    points: [
      tKey('benchmark_insight.definition.group_leisure.point1'),
      tKey('benchmark_insight.definition.group_leisure.point2'),
    ],
  },
  'benchmark_insight.definition.contract_crew': {
    name: 'contract_crew',
    description: tKey('benchmark_insight.definition.contract_crew.description'),
    points: [],
  },
  'benchmark_insight.definition.other': {
    name: 'other',
    description: tKey('benchmark_insight.definition.other.description'),
    points: [],
  },
};

export const CHANNEL_HIERARCHY = {
  HOTEL_DIRECT: 'hotel_direct',
  BRAND_DOT_COM: 'hotel_direct__branddotcom',
  CENTRAL_RESERVATION_OFFICE: 'hotel_direct__central_reservation_office',
  EMAIL: 'hotel_direct__email',
  PHONE: 'hotel_direct__phone',
  WALK_IN: 'hotel_direct__walk_in',
  HOTEL_DIRECT_OTHER: 'hotel_direct__other',
  TRAVEL_AGENT: 'travel_agent',
  OTA: 'travel_agent__ota',
  BOOKING_DOT_COM: 'travel_agent__ota__bookingdotcom',
  EXPEDIA: 'travel_agent__ota__expedia',
  HRS: 'travel_agent__ota__hrs',
  PRICELINE_AGODA: 'travel_agent__ota__priceline_agoda',
  TRIP_DOT_COM_CTRIP: 'travel_agent__ota__tripdotcom_ctrip',
  TRAVEL_AGENT_OTA_OTHER: 'travel_agent__ota__other',
  TOUR_OPERATORS: 'travel_agent__tour_operators',
  WHOLESALE: 'travel_agent__wholesale',
  TRAVEL_AGENT_OTHER: 'travel_agent__other',
  GDS: 'gds',
  AMADEUS: 'gds__amadeus',
  GALILEO: 'gds__galileo',
  SABRE: 'gds__sabre',
  WORLDSPAN: 'gds__worldspan',
  GDS_OTHER: 'gds__other',
  OTHER: 'other',
} as const;
export type ChannelHierarchyItem =
  (typeof CHANNEL_HIERARCHY)[keyof typeof CHANNEL_HIERARCHY];
export const isChannelHierarchyItem = (
  item: string,
): item is ChannelHierarchyItem => {
  return Object.values(CHANNEL_HIERARCHY).includes(
    item as ChannelHierarchyItem,
  );
};

export const CHANNEL_TRANSLATIONS: Record<ChannelHierarchyItem, string> = {
  [CHANNEL_HIERARCHY.HOTEL_DIRECT]: tKey(
    'benchmark_insight.category.hotel_direct',
  ),
  [CHANNEL_HIERARCHY.BRAND_DOT_COM]: tKey(
    'benchmark_insight.category.hotel_direct__branddotcom',
  ),
  [CHANNEL_HIERARCHY.CENTRAL_RESERVATION_OFFICE]: tKey(
    'benchmark_insight.category.hotel_direct__central_reservation_office',
  ),
  [CHANNEL_HIERARCHY.EMAIL]: tKey(
    'benchmark_insight.category.hotel_direct__email',
  ),
  [CHANNEL_HIERARCHY.PHONE]: tKey(
    'benchmark_insight.category.hotel_direct__phone',
  ),
  [CHANNEL_HIERARCHY.WALK_IN]: tKey(
    'benchmark_insight.category.hotel_direct__walk_in',
  ),
  [CHANNEL_HIERARCHY.HOTEL_DIRECT_OTHER]: tKey(
    'benchmark_insight.category.other',
  ),
  [CHANNEL_HIERARCHY.TRAVEL_AGENT]: tKey(
    'benchmark_insight.category.travel_agent',
  ),
  [CHANNEL_HIERARCHY.OTA]: tKey('benchmark_insight.category.travel_agent__ota'),
  [CHANNEL_HIERARCHY.BOOKING_DOT_COM]: tKey(
    'benchmark_insight.category.travel_agent__ota__bookingdotcom',
  ),
  [CHANNEL_HIERARCHY.EXPEDIA]: tKey(
    'benchmark_insight.category.travel_agent__ota__expedia',
  ),
  [CHANNEL_HIERARCHY.HRS]: tKey(
    'benchmark_insight.category.travel_agent__ota__hrs',
  ),
  [CHANNEL_HIERARCHY.PRICELINE_AGODA]: tKey(
    'benchmark_insight.category.travel_agent__ota__priceline_agoda',
  ),
  [CHANNEL_HIERARCHY.TRIP_DOT_COM_CTRIP]: tKey(
    'benchmark_insight.category.travel_agent__ota__tripdotcom_ctrip',
  ),
  [CHANNEL_HIERARCHY.TRAVEL_AGENT_OTA_OTHER]: tKey(
    'benchmark_insight.category.other',
  ),
  [CHANNEL_HIERARCHY.TOUR_OPERATORS]: tKey(
    'benchmark_insight.category.travel_agent__tour_operators',
  ),
  [CHANNEL_HIERARCHY.WHOLESALE]: tKey(
    'benchmark_insight.category.travel_agent__wholesale',
  ),
  [CHANNEL_HIERARCHY.TRAVEL_AGENT_OTHER]: tKey(
    'benchmark_insight.category.other',
  ),
  [CHANNEL_HIERARCHY.GDS]: tKey('benchmark_insight.category.gds'),
  [CHANNEL_HIERARCHY.AMADEUS]: tKey('benchmark_insight.category.gds__amadeus'),
  [CHANNEL_HIERARCHY.GALILEO]: tKey('benchmark_insight.category.gds__galileo'),
  [CHANNEL_HIERARCHY.SABRE]: tKey('benchmark_insight.category.gds__sabre'),
  [CHANNEL_HIERARCHY.WORLDSPAN]: tKey(
    'benchmark_insight.category.gds__worldspan',
  ),
  [CHANNEL_HIERARCHY.GDS_OTHER]: tKey('benchmark_insight.category.other'),
  [CHANNEL_HIERARCHY.OTHER]: tKey('benchmark_insight.category.other'),
};

export const DEFAULT_DAYS_SHOWN_IN_BENI = 30;

export const BENI_SEGMENTATIONS_API_IDS = {
  DATA: 'data',
  PACE: 'pace',
};

export const BENI_SEGMENTATIONS_API_URLS = {
  [BENI_SEGMENTATIONS_API_IDS.DATA]:
    '/apigateway/v1/app/benchmarkinsight/app/breakdown',
  [BENI_SEGMENTATIONS_API_IDS.PACE]:
    '/apigateway/v1/app/benchmarkinsight/app/pace',
} as const;

export const BENI_SEGMENTATIONS_API_MODEL_NAMES = {
  [BENI_SEGMENTATIONS_API_IDS.DATA]: 'benchmark-insight/data-api-response',
  [BENI_SEGMENTATIONS_API_IDS.PACE]: 'benchmark-insight/pace-api-response',
} as const;

export const BENI_COLUMN_DATA_TYPE = {
  PICKUP: 'pickup',
  REFERENCE: 'reference',
  DIFFERENCE: 'difference',
} as const;
export type BeniColumnDataType =
  (typeof BENI_COLUMN_DATA_TYPE)[keyof typeof BENI_COLUMN_DATA_TYPE];

// Note: 'market collapsed class' will be shown as 'market class' in the UI. To avoid refactoring the frontend, backend and API,
// we simply adjust the translations (which is a much smaller effort).
export const BENI_COMPARISON_SET_LONG_TRANSLATIONS: {
  [key in ComparisonSet]: string;
} = {
  [BENI_COMPARISON_SETS.OWN]: tKey('general.my_hotel'),
  [BENI_COMPARISON_SETS.COMPSET]: tKey('settings.competitors.compset'),
  [BENI_COMPARISON_SETS.INTERNAL_COMPSET]: tKey(
    'benchmark_insight.internal_compset',
  ),
  [BENI_COMPARISON_SETS.SMART_COMPSET]: tKey('benchmark_insight.smart_compset'),
  [BENI_COMPARISON_SETS.MARKET]: tKey('menu.market'),
  [BENI_COMPARISON_SETS.MARKET_CLASS]: tKey('benchmark_insight.market_class'),
  [BENI_COMPARISON_SETS.MARKET_COLLAPSED_CLASS]: tKey(
    'benchmark_insight.market_class',
  ),
  [BENI_COMPARISON_SETS.RADIUS]: tKey('benchmark_insight.radius'),
} as const;
export type BeniComparisonSetLongTranslation =
  (typeof BENI_COMPARISON_SET_LONG_TRANSLATIONS)[keyof typeof BENI_COMPARISON_SET_LONG_TRANSLATIONS];

export const COLOR_BENCHMARK_INSIGHT_DATASET_OWN = getCssVariableString(
  COLOR_CHART_SEQUENTIAL_NEUTRAL_09,
);
export const COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_OWN =
  getCssVariableString(COLOR_CHART_SEQUENTIAL_NEUTRAL_04);
export const COLOR_BENCHMARK_INSIGHT_DATASET_PRIMARY = getCssVariableString(
  COLOR_CHART_SEQUENTIAL_WARNING_05,
);
export const COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_PRIMARY =
  getCssVariableString(COLOR_CHART_SEQUENTIAL_WARNING_02);
export const COLOR_BENCHMARK_INSIGHT_DATASET_SECONDARY = getCssVariableString(
  COLOR_CHART_SEQUENTIAL_INFO_09,
);
export const COLOR_BENCHMARK_INSIGHT_DATASET_REFERENCE_SECONDARY =
  getCssVariableString(COLOR_CHART_SEQUENTIAL_INFO_04);

export const BENI_REFERENCE_PERIODS = {
  STLY: 'stly', // Same day of week last year
  SDLY: 'sdly', // Same exact date last year
} as const;

export type ReferencePeriod =
  (typeof BENI_REFERENCE_PERIODS)[keyof typeof BENI_REFERENCE_PERIODS];

// Combines the comparison set together with the reference period translation.
// For example:
// - My hotel STLY
// - Compset SDLY
// - Market class STLY
export const BENI_COMPARISON_SET_REFERENCE_PERIOD_TRANSLATIONS = {
  [BENI_COMPARISON_SETS.OWN]: tKey(
    'benchmark_insight.reference_period.my_hotel',
  ),
  [BENI_COMPARISON_SETS.COMPSET]: tKey(
    'benchmark_insight.reference_period.compset',
  ),
  [BENI_COMPARISON_SETS.INTERNAL_COMPSET]: tKey(
    'benchmark_insight.reference_period.internal_compset',
  ),
  [BENI_COMPARISON_SETS.SMART_COMPSET]: tKey(
    'benchmark_insight.reference_period.smart_compset',
  ),
  [BENI_COMPARISON_SETS.MARKET]: tKey(
    'benchmark_insight.reference_period.market',
  ),
  [BENI_COMPARISON_SETS.MARKET_CLASS]: tKey(
    'benchmark_insight.reference_period.market_class',
  ),
  [BENI_COMPARISON_SETS.MARKET_COLLAPSED_CLASS]: tKey(
    'benchmark_insight.reference_period.market_class',
  ),
  [BENI_COMPARISON_SETS.RADIUS]: tKey(
    'benchmark_insight.reference_period.radius',
  ),
};

export const BENI_REFERENCE_PERIOD_SHORT_TRANSLATIONS: Record<
  ReferencePeriod,
  string
> = {
  [BENI_REFERENCE_PERIODS.STLY]: tKey(
    'benchmark_insight.reference_period.stly.short',
  ),
  [BENI_REFERENCE_PERIODS.SDLY]: tKey(
    'benchmark_insight.reference_period.sdly.short',
  ),
};

export const BENI_REFERENCE_PERIOD_LONG_TRANSLATIONS: Record<
  ReferencePeriod,
  string
> = {
  [BENI_REFERENCE_PERIODS.STLY]: tKey(
    'benchmark_insight.reference_period.stly.long',
  ),
  [BENI_REFERENCE_PERIODS.SDLY]: tKey(
    'benchmark_insight.reference_period.sdly.long',
  ),
};

export const BENI_SEGMENT_FULL_ID = {
  TRANSIENT: 'transient',
  TRANSIENT__RETAIL: 'transient__retail',
  TRANSIENT__RETAIL__DIRECT: 'transient__retail__direct',
  TRANSIENT__RETAIL__NON_DIRECT: 'transient__retail__non_direct',
  TRANSIENT__DISCOUNT: 'transient__discount',
  TRANSIENT__DISCOUNT__GENERAL_DISCOUNT:
    'transient__discount__general_discount',
  TRANSIENT__DISCOUNT__GENERAL_DISCOUNT__DIRECT:
    'transient__discount__general_discount__direct',
  TRANSIENT__DISCOUNT__GENERAL_DISCOUNT__NON_DIRECT:
    'transient__discount__general_discount__non_direct',
  TRANSIENT__DISCOUNT__PROMOTION: 'transient__discount__promotion',
  TRANSIENT__DISCOUNT__PROMOTION__DIRECT:
    'transient__discount__promotion__direct',
  TRANSIENT__DISCOUNT__PROMOTION__NON_DIRECT:
    'transient__discount__promotion__non_direct',
  TRANSIENT__DISCOUNT__PACKAGE_PATH_OPAQUE:
    'transient__discount__package_path_opaque',
  TRANSIENT__NEGOTIATED: 'transient__negotiated',
  TRANSIENT__NEGOTIATED__CORPORATE_DISCOUNT:
    'transient__negotiated__corporate_discount',
  TRANSIENT__NEGOTIATED__CORPORATE_NEGOTIATED:
    'transient__negotiated__corporate_negotiated',
  TRANSIENT__NEGOTIATED__GOVERNMENT: 'transient__negotiated__government',
  TRANSIENT__WHOLESALE: 'transient__wholesale',
  TRANSIENT__QUALIFIED: 'transient__qualified',
  GROUP: 'group',
  GROUP__BUSINESS: 'group__business',
  GROUP__LEISURE: 'group__leisure',
  CONTRACTED_CREW: 'contracted_crew',
  OTHER: 'other',
};

export const NUMBER_FORMATS = {
  PERCENTAGE: 'percentage',
  CURRENCY: 'currency',
  RANKING: 'ranking',
  INDEX: 'index',
} as const;
export type NumberFormat = (typeof NUMBER_FORMATS)[keyof typeof NUMBER_FORMATS];

export const NUMBER_STYLES = {
  DEFAULT: 'default',
  CHANGE: 'change',
} as const;
export type NumberStyle = (typeof NUMBER_STYLES)[keyof typeof NUMBER_STYLES];

// BENI-TODO: Remove this constant once all formatNumber calls have been converted to TypeScript.
// Overview of all the ways we can format a number in Benchmark Insight.
export const SUPPORTED_FORMAT_NUMBER_COMBINATIONS = {
  [NUMBER_FORMATS.PERCENTAGE]: {
    [NUMBER_STYLES.DEFAULT]: 'percentage_default', // 0.1234 => 12% or -0.1234 => -12%
    [NUMBER_STYLES.CHANGE]: 'percentage_change', // 0.1234 => +12% or -0.1234 => -12%
  },
  [NUMBER_FORMATS.CURRENCY]: {
    [NUMBER_STYLES.DEFAULT]: 'currency_default', // 1234.5678 => $1,234 or -1234.5678 => -$1,234
    [NUMBER_STYLES.CHANGE]: 'currency_change', // 1234.5678 => +$1,234 or -1234.5678 => -$1,234
  },
  [NUMBER_FORMATS.RANKING]: {
    [NUMBER_STYLES.DEFAULT]: 'ranking_default', // 7 => 7 (can never be negative)
    [NUMBER_STYLES.CHANGE]: 'ranking_change', // 7 => +7 or -7 => -7
  },
  [NUMBER_FORMATS.INDEX]: {
    [NUMBER_STYLES.DEFAULT]: 'index_default', // 1.3634 => 136 or 0.5464 => 54 (can never be negative)
  },
};

// STAY_DATE_RANGE is the only reason that the user can fix themself, by changing the date range selection.
// The rest is an internal reason in backend, meant for debugging.
export const COMPARISON_SET_MISSING_REASONS = {
  BENI_READINESS: 'beni_readiness', // Not enough PMS Integrations are ready for Benchmark Insight.
  CURRENCY: 'currency', // Not enough PMS Integrations use the same currency.
  MAPPING_COVERAGE: 'mapping_coverage', // Not enough PMS Integrations have enough mapping coverage.
  STAY_DATE_RANGE: 'stay_date_range', // Not enough PMS Integrations in requested stay date range.
  EXTRACT_DATE: 'extract_date', // Not enough PMS Integrations with data for the given extract date.
  MARKET_SHARE: 'market_share', // Not enough PMS Integrations left after removing those from the chain with dominant market share.
  COMPSET_VALIDITY: 'compset_validity', // The configured compset is not valid, because it breaks one or more of the legal compset rules.
} as const;
export type ComparisonSetMissingReason =
  (typeof COMPARISON_SET_MISSING_REASONS)[keyof typeof COMPARISON_SET_MISSING_REASONS];

export const COMPARISON_SET_DATA_POINTS_MISSING_REASONS = {
  NOT_ENOUGH_HOTELS: 'datapoint.not_enough_hotels',
} as const;
export type ComparisonSetDataPointsMissingReason =
  (typeof COMPARISON_SET_DATA_POINTS_MISSING_REASONS)[keyof typeof COMPARISON_SET_DATA_POINTS_MISSING_REASONS];

export const BENI_MARKET_CLASS_TRANSLATIONS = {
  [BENI_MARKET_CLASS.LUXURY]: tKey('benchmark_insight.hotel_class.luxury'),
  [BENI_MARKET_CLASS.UPPER_UPSCALE]: tKey(
    'benchmark_insight.hotel_class.upper_upscale',
  ),
  [BENI_MARKET_CLASS.UPSCALE]: tKey('benchmark_insight.hotel_class.upscale'),
  [BENI_MARKET_CLASS.UPPER_MIDSCALE]: tKey(
    'benchmark_insight.hotel_class.upper_midscale',
  ),
  [BENI_MARKET_CLASS.MIDSCALE]: tKey('benchmark_insight.hotel_class.midscale'),
  [BENI_MARKET_CLASS.ECONOMY]: tKey('benchmark_insight.hotel_class.economy'),
} as const;

export const BENI_CHARTS_HEIGHT = '270px';

export const BENI_CHARTS_Y_AXIS_LABEL_MIN_WIDTH = '30px';

export const BENI_CHARTS_LABEL_COLOR = getCssVariableString(COLOR_TEXT_SUBDUED);

export const BENI_CHARTS_AXIS_TITLE_COLOR =
  getCssVariableString(COLOR_TEXT_DEFAULT);

export const BENI_CHARTS_GRID_LINE_COLOR =
  getCssVariableString(COLOR_BORDER_DEFAULT);

export const BENI_CHARTS_CUSTOM_LEGEND_CSS_SELECTORS = {
  OWN: 'benchmark-insight-dataset-own',
  PRIMARY: 'benchmark-insight-dataset-primary',
  SECONDARY: 'benchmark-insight-dataset-secondary',
  OWN_REFERENCE: 'benchmark-insight-dataset-own-reference',
  PRIMARY_REFERENCE: 'benchmark-insight-dataset-primary-reference',
  SECONDARY_REFERENCE: 'benchmark-insight-dataset-secondary-reference',
};

export const BENI_STAY_DATE_SECTION_VIEWS = {
  CHART: 'chart',
  TABLE: 'table',
} as const;
export type BeniStayDateSectionView =
  (typeof BENI_STAY_DATE_SECTION_VIEWS)[keyof typeof BENI_STAY_DATE_SECTION_VIEWS];

export const BENI_COMPETITORS_TABS = {
  COMPSET: 'compset',
  PORTFOLIO_COMPSET: 'portfolio_compset',
  SMART_COMPSET: 'smart_compset',
} as const;
export type CompetitorsBenchmarkTab =
  (typeof BENI_COMPETITORS_TABS)[keyof typeof BENI_COMPETITORS_TABS];

export const BENI_PICKUP_DAYS = {
  ONE: 1,
  THREE: 3,
  SEVEN: 7,
  FOURTEEN: 14,
  THIRTY: 30,
};

export const BENI_PICKUP_DAYS_DEFAULT = BENI_PICKUP_DAYS.SEVEN;

export const BREAKDOWNS = {
  STAY_DATE: 'stay_date',
  PACE: 'pace',
  SEGMENT: 'segment',
  LENGTH_OF_STAY: 'los',
  CHANNEL: 'channel',
} as const;
export type BeniBreakdownType = (typeof BREAKDOWNS)[keyof typeof BREAKDOWNS];

export const BREAKDOWNS_WITH_HIERARCHY: BeniBreakdownType[] = [
  BREAKDOWNS.SEGMENT,
  BREAKDOWNS.CHANNEL,
];
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const isBreakdownWithHierarchy = (breakdown: BeniBreakdownType) => {
  return BREAKDOWNS_WITH_HIERARCHY.includes(breakdown);
};

export const BREAKDOWN_TRANSLATIONS: Record<BeniBreakdownType, string> = {
  [BREAKDOWNS.STAY_DATE]: tKey(
    'benchmark_insight.headers.detailed_view_stay_date',
  ),
  [BREAKDOWNS.PACE]: tKey('general.pace'),
  [BREAKDOWNS.SEGMENT]: tKey('benchmark_insight.segmentation'),
  [BREAKDOWNS.LENGTH_OF_STAY]: tKey('benchmark_insight.los.breakdown-title'),
  [BREAKDOWNS.CHANNEL]: tKey('benchmark_insight.breakdown.channel'),
};

export const HIDDEN_FUTURE_COMPSET_DATA_TRANSLATIONS: Partial<
  Record<Kpi, string>
> = {
  [KPIS.ADR]: tKey(
    'benchmark_insight.hidden_future_data.legend_item_tooltip.adr',
  ),
  [KPIS.REVPAR]: tKey(
    'benchmark_insight.hidden_future_data.legend_item_tooltip.revpar',
  ),
};

export const ROUTE_BENCHMARK_INSIGHT_INDEX = 'hotel.benchmark-insight.index';
export const ROUTE_BENCHMARK_INSIGHT_COMPSET_BUILDER =
  'hotel.settings.competitors.benchmark.compset.builder.index';

export const BREAKDOWN_CHART_HEIGHT = 250;
export const BREAKDOWN_CHART_COLUMN_SERIES_POINT_PADDING = 0.1;
export const BREAKDOWN_CHART_COLUMN_SERIES_GROUP_PADDING = 0.2;
